/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AOMGH5JsErrorPlugin
 */

import { IContext } from '@omega-tracker/core/src/com/didichuxing/context/IContext';
import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { ITrackerAdvancedContext } from 'com/didichuxing/tracker/h5/context/ITrackerAdvancedContext';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import AReportingStrategyPlugin from 'com/didichuxing/tracker/plugin/AReportingStrategyPlugin';
import OMGH5JsErrorEvent from 'com/didichuxing/tracker/h5/plugins/events/OMGH5JsErrorEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
import UUID from 'com/didichuxing/tracker/plugin/utils/UUID';
import Md5 from 'com/didichuxing/tracker/plugin/utils/MD5';

export default abstract class AOMGH5JsErrorPlugin extends AReportingStrategyPlugin {
    /**
     * 错误类型
     */
    private typeList: string[] = ['global code', 'TypeError',
        'SyntaxError', 'ReferenceError', 'RangeError', 'URIError', 'EvalError'];

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public getName(): string {
        return PluginUtils.PLUGIN_NAME_JS_ERROR;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public install(): boolean {
        this.handleError = this.handleError.bind(this);
        this.handlePromiseError = this.handlePromiseError.bind(this);
        this.afterReport = this.afterReport.bind(this);
        if ((this.getContext() as ITrackerContext).autoError) {
            window.addEventListener('error', this.handleError);
            window.addEventListener('unhandledrejection', this.handlePromiseError);
            this.proxyConsoleError();
            this.reportbeforeError();
        }
        return super.install();
    }

    /**
     * 发送onloan之前的错误
     */
    public reportbeforeError(): void {
        const beforeErrorList = this.getTracker().beforeErrorList || [];
        beforeErrorList.forEach((event) => {
            this.handleError(event, 'beforeLoad');
        });
        this.getTracker().beforeErrorList = [];
    }

    /**
     * 发送错误数据进行hook
     */
    private afterReport(event: any): void {
        const context: any = this.getContext();
        if (context) {
            if (context.errorHook) {
                // 防止报错将回调进行包裹
                try {
                    context.errorHook(event);
                } catch (error) {
                    console.info('回调异常');
                }
            }
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IContext}
     */
    protected get initialzedContext(): IContext {
        const context: ITrackerAdvancedContext = this.getContext() as ITrackerAdvancedContext;
        return {
            ...super.initialzedContext,
            autoError: typeof context.autoError === 'boolean' ? context.autoError : true,
            jsErrorFilters: Array.isArray(context.jsErrorFilters) ? context.jsErrorFilters : []
        } as IContext;
    }

    /**
     * @override
     * @inheritDoc
     */
    public execute(): void {
        /* Nothing to execute */
    }

    /**
     * @override
     * @inheritDoc
     */
    public exit(): void {
        /* Nothing to exit */
    }

    /**
     * 获取Breadcrumb
     */
    private getBreadcrumbList(): string[] {
        const breadcrumbListName = (window as any)[this.breadcrumbListName] || [];
        if (breadcrumbListName.length > 10) {
            (window as any)[this.breadcrumbListName].length = 10;
        }
        return breadcrumbListName;
    }

    /**
     * 代理console.error
     */
    private proxyConsoleError() {
        const t = this;
        // 关闭的话不进行监听
        if ((t.getContext() as any).autoConsoleError === false) {
            return;
        }
        if (console && typeof console === 'object') {
            try {
                const fn = console.error;
                console.error = function() {
                    try {
                        const errEvent = arguments[0];
                        if (errEvent && errEvent.message && errEvent.stack) {
                            t.handleError({
                                type: 'error',
                                error: errEvent,
                                message: errEvent.message
                            });
                        }
                    } catch (error) {
                        console.info(`代理console.error失败,${error}`);
                    }
                    return fn.apply(this, arguments as any);
                };
            } catch (error) {
                console.info(`代理console.error失败,${error}`);
            }
        }
    }

    /**
     * 获取错误类型
     * @param event
     */
    private getErrorType(event: any): string {
        const { message, error } = event;
        const eventStack = error && error.stack ? error.stack : '';
        const eventMessage = message || '';
        const errorMessage: string = `${eventStack}${eventMessage}`;
        let result: string = 'NoErrorType'; // 默认错误类型
        if (eventStack.startsWith('Error:') || eventMessage.startsWith('Error:')) { // 网络错误
            if (errorMessage.indexOf('Request failed') !== -1 ||
                errorMessage.indexOf('Network Error') !== -1
            ) { // 部分网络错误
                result = 'NetworkError';
            } else { // 其余网络错误
                result = 'Error';
            }
        } else if (errorMessage.indexOf('Script error') !== -1) { // script error
            result = 'ScriptError';
        } else {
            this.typeList.forEach((type: string): void => {
                if (errorMessage.indexOf(type) !== -1) {
                    result = type;
                }
            });
        }
        return result;
    }

    /**
     * handle promise error.
     * - 类型promiseRejectionEvent由于typescript的原因改为any
     */
     protected handlePromiseError(promiseRejectionEvent: any, occasion: string = 'afterLoad'): void {
        const event = promiseRejectionEvent.reason || {};
        // 非正常的错误不进行上报
        if (!event.message || !event.stack) {
            return;
        }
        // 过滤ResizeObserver 错误的上报。在 Chrome 83 无限上报会造成浏览器卡死
        if (event.message && event.message.indexOf('ResizeObserver') !== -1) {
          return;
        }
        // 过滤正则匹配后的错误
        const context: ITrackerAdvancedContext = this.getContext() as ITrackerAdvancedContext;
        if (context.jsErrorFilters) {
            // 标记是否需要过滤掉
            let isFilter: boolean = false;
            context.jsErrorFilters.forEach(k => {
                const message = k.message;
                if (message instanceof RegExp && message.test(event.message)) {
                    isFilter = true;
                }
            });
            if (isFilter) {
                return;
            }
        }
        const type = this.getErrorType(event);
        this.report({
            eventId: PluginUtils.EVENT_ID_JS_ERROR,
            attrs: {
                label: event.stack,
                message: 'Uncaught (in promise) ' + event.message,
                stack: event.stack || '',
                breadcrumb: this.getBreadcrumbList(),
                UUID: UUID.v4,
                type: type,
                typeMd5: Md5.hashStr(type),
                occasion: occasion
            }
        }).then(this.afterReport);
    }

    /**
     * handle js error.
     */
    protected handleError(event: ErrorEvent | any, occasion: string = 'afterLoad'): void {
        // 过滤ResizeObserver 错误的上报。在 Chrome 83 无限上报会造成浏览器卡死
        if (event.message && event.message.indexOf('ResizeObserver') !== -1) {
          return;
        }
        // 过滤正则匹配后的错误
        const context: ITrackerAdvancedContext = this.getContext() as ITrackerAdvancedContext;
        if (context.jsErrorFilters) {
            // 标记是否需要过滤掉
            let isFilter: boolean = false;
            context.jsErrorFilters.forEach(k => {
                const message = k.message;
                if (message instanceof RegExp && message.test(event.message)) {
                    isFilter = true;
                }
            });
            if (isFilter) {
                return;
            }
        }
        const type = this.getErrorType(event);
        this.report({
            eventId: PluginUtils.EVENT_ID_JS_ERROR,
            attrs: {
                label: event.error ? event.error.stack : event.message,
                line: event.lineno,
                url: event.filename,
                col: event.colno,
                message: event.message,
                stack: event.error ? (event.error.stack || '') : '',
                breadcrumb: this.getBreadcrumbList(),
                UUID: UUID.v4,
                type: type,
                typeMd5: Md5.hashStr(type),
                occasion: occasion
            }
        }).then(this.afterReport);
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {ITrackedEvent}
     */
    protected generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent {
        return new OMGH5JsErrorEvent(eventArgs.attrs, this.getContext());
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public uninstall(): boolean {
        window.removeEventListener('error', this.handleError);
        window.removeEventListener('unhandledrejection', this.handlePromiseError);
        return super.uninstall();
    }
}
